import { PrintView } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ForwardedRef, forwardRef, ReactElement, useCallback } from 'react';
import { getArticleDataTestId } from '../../../components/ArticlePreview/ArticlePreview.logic';
import AnalystsWidget from '../../../components/widgets/Analysts';
import { Article } from '../../../models';
import { getMorningCommentRegion } from '../../../utils/tagUtils';
import { formatDateTime, isSectorReview } from '../Article.logic';
import ArticleContent from '../ArticleContent';
import ArticleIncludedContent from '../ArticleIncludedContent/ArticleIncludedContent';
import { isHeaderFooterVisible } from './ArticlePrintView.logic';
import styles from './ArticlePrintView.module.scss';
import RestrictedArticleView from '../../../components/RestrictedArticleAccess/RestrictedArticleView';

const cx = classNames.bind(styles);

export interface ArticlePrintViewProps {
  article: Article | undefined;
  userFullName?: string;
  disclaimer?: string;
  redistributionAllowed?: boolean;
  isArticlePreview?: boolean;
  setIsAnalystWidgetLoading?: (value: boolean) => void;
}

const ArticlePrintView = (
  {
    article,
    userFullName,
    disclaimer,
    redistributionAllowed,
    isArticlePreview = false,
    setIsAnalystWidgetLoading,
  }: ArticlePrintViewProps,
  ref: ForwardedRef<HTMLElement>
): ReactElement | null => {
  const formattedPublishDate = article && formatDateTime(article);
  const isMorningComment = article && !!getMorningCommentRegion(article.tags);

  const articlePrintRef = useCallback((node) => {
    if (node !== null) {
      node.querySelectorAll('img').forEach((img) => {
        img?.closest('p')?.classList.add('no-break');
      });
    }
  }, []);

  if (!article) {
    return null;
  }

  return (
    <PrintView
      ref={ref}
      isHeaderFooterVisible={isHeaderFooterVisible(article.articleType)}
      redistributionAllowed={redistributionAllowed}
      userFullName={userFullName}
      disclaimer={disclaimer}
    >
      {article.canAccess ? (
        <div
          className={cx('body')}
          data-testid={getArticleDataTestId('article-print-view', isArticlePreview)}
          ref={articlePrintRef}
        >
          <div className={cx('header')}>
            <h1
              className={cx('title')}
              data-testid={getArticleDataTestId('article-print-title', isArticlePreview)}
              dangerouslySetInnerHTML={{ __html: article.content.title }}
            />
            <div className={cx('subtitle')}>
              <div className={cx('article-type')} data-testid="article-type">
                {article.articleType}
              </div>
              <div
                className={cx('publish-date')}
                data-testid={getArticleDataTestId('article-print-publish-date', isArticlePreview)}
              >
                {formattedPublishDate}
              </div>
              {!isMorningComment && (
                <AnalystsWidget
                  analysts={article.analysts}
                  canAccess={article.canAccess}
                  print
                  setIsArticleAnalystWidgetLoading={setIsAnalystWidgetLoading}
                />
              )}
            </div>
          </div>
          <ArticleContent
            content={article.content}
            bodyClassName={cx('content')}
            summaryClassName={cx('content-summary')}
            articleBodyClassName={cx('article-body')}
            isSectorReview={isSectorReview(article)}
            print
          />
          <ArticleIncludedContent
            includedContent={article.content.includedContent}
            print
            isSectorReview={isSectorReview(article)}
          />
        </div>
      ) : (
        <RestrictedArticleView articlePermissions={article} />
      )}
    </PrintView>
  );
};

export default forwardRef(ArticlePrintView);
